import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";

const GardenNote = () => {
  const { slug } = useParams();
  const [note, setNote] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [toc, setToc] = useState([]);

  // Generate slug from text
  const generateSlug = useCallback((text) => {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  }, []);

  // Extract headers from HTML and generate TOC
  const generateTOC = useCallback((htmlString) => {
    const temp = document.createElement("div");
    temp.innerHTML = htmlString;
    const headers = temp.querySelectorAll("h2, h3, h4, h5, h6");
    const tocItems = [];

    headers.forEach((header) => {
      const level = parseInt(header.tagName[1]);
      const text = header.textContent;
      const id = generateSlug(text);
      header.id = id;

      tocItems.push({
        id,
        text,
        level,
      });
    });

    setToc(tocItems);
    return temp.innerHTML;
  }, [generateSlug]);

  // Nest TOC items by level
  const nestTOC = (items) => {
    if (items.length === 0) return [];

    const result = [];
    const stack = []; // Stack of [item, childrenArray] pairs

    items.forEach((item) => {
      const newItem = { ...item, children: [] };

      // Pop stack until we find a parent or stack is empty
      while (stack.length > 0 && stack[stack.length - 1].level >= item.level) {
        stack.pop();
      }

      if (stack.length === 0) {
        // Top-level item
        result.push(newItem);
      } else {
        // Add as child to current top of stack
        const parent = stack[stack.length - 1];
        parent.children.push(newItem);
      }

      stack.push(newItem);
    });

    return result;
  };

  // Render TOC as nested list
  const renderTOC = (items) => {
    if (items.length === 0) return null;

    return (
      <ul className="toc-list">
        {items.map((item) => (
          <li key={item.id} className={`toc-level-${item.level}`}>
            <a href={`#${item.id}`}>{item.text}</a>
            {item.children && item.children.length > 0 && renderTOC(item.children)}
          </li>
        ))}
      </ul>
    );
  };

  useEffect(() => {
    const loadNote = async () => {
      try {
        // Fetch HTML content
        const htmlResponse = await fetch(`/garden/generated/notes/${slug}.html`);
        if (!htmlResponse.ok) throw new Error(`Note "${slug}" not found`);
        let html = await htmlResponse.text();
        
        // Extract content div and generate TOC
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;
        const contentDiv = tempDiv.querySelector(".content");
        if (contentDiv) {
          const cleanedHtml = generateTOC(contentDiv.innerHTML);
          tempDiv.querySelector(".content").innerHTML = cleanedHtml;
          html = tempDiv.innerHTML;
        }
        
        setNote(html);

        // Fetch metadata from index
        const indexResponse = await fetch(`/garden/generated/index.json`);
        if (indexResponse.ok) {
          const indexData = await indexResponse.json();
          const noteMetadata = indexData.notes[slug];
          if (noteMetadata) {
            setMetadata(noteMetadata);
          }
        }

        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    loadNote();
  }, [slug, generateTOC]);

  if (loading) {
    return (
      <div className="garden-page">
        <div className="title">
          <h2 className="heading">loading... 🌱</h2>
        </div>
        <div className="garden-content">
          <p>Loading your note...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="garden-page">
        <div className="title">
          <h2 className="heading">note not found. 🌿</h2>
        </div>
        <div className="garden-content">
          <p>Error: {error}</p>
          <Link to="/garden/index" className="garden-back-link" style={{ marginTop: "20px" }}>
            ← Back to Garden
          </Link>
        </div>
      </div>
    );
  }

  const maturityEmoji = {
    seed: "🌱",
    sapling: "🌿",
    shrub: "🌳",
  };

  // Extract title from metadata
  const getTitle = () => {
    return metadata?.title || "Untitled";
  };

  return (
    <div className="garden-page">
      <Link to="/garden/index" className="garden-back-link" style={{ margin: "20px" }}>
        ← Back to index
      </Link>
      
      {/* Mobile: Title and Metadata Toggle */}
      <div className="garden-note-mobile-header">
        <h1 className="garden-note-title">{getTitle()}</h1>
        <button 
          className="garden-note-metadata-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label="Toggle metadata sidebar"
        >
          <span className="toggle-icon">◀</span> DETAILS
        </button>
      </div>

      {/* Collapsible Metadata Sidebar (Mobile) */}
      {sidebarOpen && (
        <div className="garden-note-mobile-metadata">
          <div className="sidebar-section">
            {/* <h4>Details</h4> */}
            {metadata && (
              <>
                <div className="metadata-item">
                  <span className="metadata-label">Status</span>
                  <span className="metadata-value">
                    {maturityEmoji[metadata.maturity]} {metadata.maturity}
                  </span>
                </div>
                <div className="metadata-item">
                  <span className="metadata-label">Planted</span>
                  <span className="metadata-value">
                    {new Date(metadata.plantedDate).toLocaleDateString()}
                  </span>
                </div>
                <div className="metadata-item">
                  <span className="metadata-label">Tended</span>
                  <span className="metadata-value">
                    {new Date(metadata.tendedDate).toLocaleDateString()}
                  </span>
                </div>
              </>
            )}
          </div>

          {metadata && metadata.tags.length > 0 && (
            <div className="sidebar-section">
              <h4>Tags</h4>
              <div className="tags-list">
                {metadata.tags.map((tag) => (
                  <Link key={tag} to={`/garden/index?tag=${tag}`} className="tag-badge">
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {metadata && metadata.backlinks.length > 0 && (
            <div className="sidebar-section">
              <h4>Linked From</h4>
              <ul className="backlinks-list">
                {metadata.backlinks.map((backlink) => (
                  <li key={backlink}>
                    <a href={`/garden/note/${backlink}`}>{backlink}</a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="garden-note-layout">
        {/* Left Column - Metadata */}
        <aside className="garden-note-sidebar">
          <div className="sidebar-section">
            <h4>Details</h4>
            {metadata && (
              <>
                <div className="metadata-item">
                  <span className="metadata-label">Status</span>
                  <span className="metadata-value">
                    {maturityEmoji[metadata.maturity]} {metadata.maturity}
                  </span>
                </div>
                <div className="metadata-item">
                  <span className="metadata-label">Planted</span>
                  <span className="metadata-value">
                    {new Date(metadata.plantedDate).toLocaleDateString()}
                  </span>
                </div>
                <div className="metadata-item">
                  <span className="metadata-label">Tended</span>
                  <span className="metadata-value">
                    {new Date(metadata.tendedDate).toLocaleDateString()}
                  </span>
                </div>
              </>
            )}
          </div>

          {metadata && metadata.tags.length > 0 && (
            <div className="sidebar-section">
              <h4>Tags</h4>
              <div className="tags-list">
                {metadata.tags.map((tag) => (
                  <Link key={tag} to={`/garden/index?tag=${tag}`} className="tag-badge">
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {toc.length > 0 && (
            <div className="sidebar-section">
              <h4>Contents</h4>
              {renderTOC(nestTOC(toc))}
            </div>
          )}

          {metadata && metadata.backlinks.length > 0 && (
            <div className="sidebar-section">
              <h4>Linked From</h4>
              <ul className="backlinks-list">
                {metadata.backlinks.map((backlink) => (
                  <li key={backlink}>
                    <a href={`/garden/note/${backlink}`}>{backlink}</a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>

        {/* Middle Column - Content */}
        <article className="garden-note-container">
          {/* Desktop: Show title at top of content */}
          <h1 className="garden-note-desktop-title">{getTitle()}</h1>
          <div
            className="garden-note-content"
            dangerouslySetInnerHTML={{ __html: note }}
          />
        </article>

        {/* Right Column - Placeholder */}
        <aside className="garden-note-sidebar-right">
          <div className="sidebar-section">
            <h4>Related Notes</h4>
            <p className="placeholder-text">Still being developed...</p>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default GardenNote;
